import { FC, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Drawer, Dropdown, Row, Space, Tag, Typography } from 'antd';
import { AxiosResponse } from 'axios';
import { TbCopy, TbExternalLink, TbFilePlus, TbFileStack, TbFileText, TbLink, TbQuote } from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { authorizedAxiosInstance } from '@/clients';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSourceQuoteActions } from '@/hooks/useSourceQuoteActions';
import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import {
    CompactCollapsible,
    ContextualCommentButton,
    EmptySection,
    VirtualizedList as List,
    MiddleDot,
} from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import DocumentReaderFooter from '@/shared/components/layouts/footers/DocumentReaderFooter';
import QuoteMemoDropdownPanel from '@/shared/components/memos/QuoteMemoDropdownPanel';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { useMemoStore } from '@/stores';
import type { ContextualCommentTarget } from '@/types';
import { dayjs } from '@/utilities';

import FilingFiltersToolbar from './components/FilingFiltersToolbar';
import { filingCategories } from './filingCategories';
import {
    findFilingFinancialSource,
    findFilingFinancialStatementSection,
    focusFilingFinancialSource,
    focusFilingFinancialStatementSection,
} from './financialStatementSource';
import type { FilingFinancialSourceLocator, FilingFinancialStatementKind } from './financialStatementSource';
import type { FilingQuoteSelection, FindableWindow, SECFiling, SECFilingsExplorerProps } from './types';
import {
    applyLightFilingDocumentSurface,
    clearFilingSourceParams,
    FILING_DOCUMENT_BACKGROUND,
    filterFilings,
    getFilingIdentity,
    getFilingQuoteHtml,
    getFilingSourceDateRange,
    getFilingSourceKey,
    getFilingType,
    getFilingUrl,
    getPopulatedFilingSections,
    matchesFilingSource,
    normalizeSelectionText,
    shouldOpenFilingFromSource,
} from './utils';

const filingFinancialStatementKinds = new Set<string>(['balance-sheet', 'cash-flow', 'income-statement']);
const FILING_INITIAL_PAGE_COUNT = 6;
const FILING_HISTORY_BATCH_PAGE_COUNT = 4;
const FILING_HISTORY_MAX_PAGE_COUNT = 100;
const FILING_HISTORY_YEARS = 35;
const FILING_PAGE_SIZE = 100;
const FILING_SECTION_PAGE_SIZE = 50;
const FILING_SECTION_SCROLL_HEIGHT = 'min(32rem, 65vh)';

const isFilingFinancialStatementKind = (value: string | null): value is FilingFinancialStatementKind =>
    Boolean(value && filingFinancialStatementKinds.has(value));

const mergeFilings = (...filingCollections: SECFiling[][]) => {
    const filingsByKey = new Map<string, SECFiling>();

    filingCollections.flat().forEach((filing) => {
        filingsByKey.set(getFilingIdentity(filing), filing);
    });

    return Array.from(filingsByKey.values()).sort(
        (left, right) =>
            dayjs(right.filingDate ?? right.acceptedDate).valueOf() -
            dayjs(left.filingDate ?? left.acceptedDate).valueOf(),
    );
};

interface SECFilingResponseEnvelope {
    data?: SECFiling[];
}

const getResponseFilings = (responseData: SECFiling[] | SECFilingResponseEnvelope) =>
    Array.isArray(responseData) ? responseData : (responseData.data ?? []);

const SECFilingsExplorer: FC<SECFilingsExplorerProps> = ({
    commentResource,
    drawerOnly = false,
    filingsPathname,
    sourceEntity,
    symbol,
}): JSX.Element => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const viewerRef = useRef<HTMLDivElement>(null);
    const drawerSourceKeyRef = useRef<string>();
    const dismissedSourceKeyRef = useRef<string>();
    const filingsAbortControllerRef = useRef<AbortController>();
    const filingsHaveMoreRef = useRef(true);
    const filingsLoadingMoreRef = useRef(false);
    const filingsSymbolRef = useRef<string>();
    const nextFilingPageRef = useRef(FILING_INITIAL_PAGE_COUNT);
    const [loading, setLoading] = useState(true);
    const [hasMoreFilings, setHasMoreFilings] = useState(true);
    const [loadingMoreFilings, setLoadingMoreFilings] = useState(false);
    const [loadingInvestorMaterials, setLoadingInvestorMaterials] = useState(false);
    const [loadingDocument, setLoadingDocument] = useState(false);
    const [filings, setFilings] = useState<SECFiling[]>([]);
    const [selectedFiling, setSelectedFiling] = useState<SECFiling>();
    const [selectedFilingDocument, setSelectedFilingDocument] = useState('');
    const [selectedFilingDocumentUrl, setSelectedFilingDocumentUrl] = useState('');
    const [quoteSelection, setQuoteSelection] = useState<FilingQuoteSelection>();
    const [openCategoryKeys, setOpenCategoryKeys] = useState<Set<string>>(() => new Set());
    const [filingFilters, setFilingFilters] = useState<ListFilterRule[]>([]);
    const [filingQuery, setFilingQuery] = useState('');
    const [resolvedSourceLabel, setResolvedSourceLabel] = useState<string>();
    const [sourceResolutionAttempted, setSourceResolutionAttempted] = useState(false);

    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const { fetchMemos, memos, loading: loadingMemos } = useMemoStore();

    const sourceParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const sourceAcceptedDate = sourceParams.get('acceptedDate');
    const sourceFilingCik = sourceParams.get('filingCik');
    const sourceFilingDate = sourceParams.get('filingDate');
    const sourceFilingSymbol = sourceParams.get('filingSymbol');
    const sourceFilingType = sourceParams.get('filingType');
    const sourceFilingUrl = sourceParams.get('filingUrl');
    const sourceFilingText = sourceParams.get('filingText');
    const sourceStatement = sourceParams.get('sourceStatement');
    const sourceAsReportedLabel = sourceParams.get('sourceAsReportedLabel');
    const sourceLineItem = sourceParams.get('sourceLineItem');
    const sourceLabel = sourceParams.get('sourceLabel');
    const sourcePeriod = sourceParams.get('sourcePeriod');
    const sourceValueParam = sourceParams.get('sourceValue');
    const filingLookupSymbol = sourceFilingSymbol ?? symbol;
    const filingLookupKey = `${sourceFilingCik ?? ''}:${filingLookupSymbol ?? ''}`;
    const sourceValue = sourceValueParam === null ? undefined : Number(sourceValueParam);
    const sourceStatementKind = isFilingFinancialStatementKind(sourceStatement) ? sourceStatement : undefined;
    const sourceFinancialLocator = useMemo<FilingFinancialSourceLocator | undefined>(() => {
        if (!sourceStatementKind || typeof sourceValue !== 'number' || !Number.isFinite(sourceValue)) {
            return undefined;
        }

        return {
            asReportedLabel: sourceAsReportedLabel,
            lineItemKey: sourceLineItem,
            lineItemLabel: sourceLabel,
            period: sourcePeriod,
            statementKind: sourceStatementKind,
            value: sourceValue,
        };
    }, [sourceAsReportedLabel, sourceLabel, sourceLineItem, sourcePeriod, sourceStatementKind, sourceValue]);
    const sourceStatementSectionLocator = useMemo<FilingFinancialSourceLocator | undefined>(() => {
        if (!sourceStatementKind || sourceFinancialLocator) {
            return undefined;
        }

        return {
            period: sourcePeriod,
            statementKind: sourceStatementKind,
        };
    }, [sourceFinancialLocator, sourcePeriod, sourceStatementKind]);
    const sourceLocator = useMemo(
        () => ({
            acceptedDate: sourceAcceptedDate,
            filingCik: sourceFilingCik,
            filingDate: sourceFilingDate,
            filingSymbol: sourceFilingSymbol,
            filingType: sourceFilingType,
            filingUrl: sourceFilingUrl,
            sourceAsReportedLabel,
            sourceLineItem,
            sourceLabel,
            sourcePeriod,
            sourceStatement,
            sourceValue: sourceValueParam,
        }),
        [
            sourceAcceptedDate,
            sourceFilingCik,
            sourceFilingDate,
            sourceFilingSymbol,
            sourceFilingType,
            sourceFilingUrl,
            sourceAsReportedLabel,
            sourceLineItem,
            sourceLabel,
            sourcePeriod,
            sourceStatement,
            sourceValueParam,
        ],
    );
    const sourceKey = getFilingSourceKey(sourceLocator);
    const sourceDateRange = useMemo(() => getFilingSourceDateRange(sourceLocator), [sourceLocator]);
    const canViewInFilings = Boolean(
        drawerOnly && filingsPathname && location.pathname !== filingsPathname && selectedFiling,
    );
    const resolvingSourceFiling = Boolean(drawerOnly && sourceKey && !selectedFiling);

    const getFilingPage = useCallback(
        async (page: number, signal?: AbortSignal) => {
            if (!filingLookupSymbol && !sourceFilingCik) {
                return [];
            }

            const response: AxiosResponse<SECFiling[] | SECFilingResponseEnvelope> = await authorizedAxiosInstance.get(
                `/api/vendors/fmp/sec-filings/${encodeURIComponent(filingLookupSymbol ?? `CIK${sourceFilingCik}`)}`,
                {
                    params: {
                        cik: sourceFilingCik ?? undefined,
                        from: dayjs().subtract(FILING_HISTORY_YEARS, 'year').format('YYYY-MM-DD'),
                        limit: FILING_PAGE_SIZE,
                        page,
                        to: dayjs().format('YYYY-MM-DD'),
                    },
                    signal,
                },
            );

            return getResponseFilings(response.data);
        },
        [filingLookupSymbol, sourceFilingCik],
    );

    const setFilingsHaveMore = useCallback((hasMore: boolean) => {
        filingsHaveMoreRef.current = hasMore;
        setHasMoreFilings(hasMore);
    }, []);

    const loadMoreFilings = useCallback(async () => {
        const controller = filingsAbortControllerRef.current;

        if (
            (!filingLookupSymbol && !sourceFilingCik) ||
            !controller ||
            controller.signal.aborted ||
            !filingsHaveMoreRef.current ||
            filingsLoadingMoreRef.current
        ) {
            return;
        }

        const firstPage = nextFilingPageRef.current;

        if (firstPage >= FILING_HISTORY_MAX_PAGE_COUNT) {
            setFilingsHaveMore(false);

            return;
        }

        const lastPage = Math.min(firstPage + FILING_HISTORY_BATCH_PAGE_COUNT, FILING_HISTORY_MAX_PAGE_COUNT);
        const pages = Array.from({ length: lastPage - firstPage }, (_, index) => firstPage + index);
        const requestedSymbol = filingLookupKey;

        nextFilingPageRef.current = lastPage;
        filingsLoadingMoreRef.current = true;
        setLoadingMoreFilings(true);

        try {
            const results = await Promise.allSettled(pages.map((page) => getFilingPage(page, controller.signal)));

            if (controller.signal.aborted || filingsSymbolRef.current !== requestedSymbol) {
                return;
            }

            const firstRejectedRequest = results.find(
                (result): result is PromiseRejectedResult => result.status === 'rejected',
            );

            if (firstRejectedRequest) {
                captureException(firstRejectedRequest.reason);
            }

            const loadedPages = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
            const historicalFilings = loadedPages.flat();

            if (historicalFilings.length) {
                setFilings((currentFilings) => mergeFilings(currentFilings, historicalFilings));
            }

            const reachedLastPage =
                lastPage >= FILING_HISTORY_MAX_PAGE_COUNT ||
                loadedPages.some((pageFilings) => pageFilings.length < FILING_PAGE_SIZE);

            if (reachedLastPage) {
                setFilingsHaveMore(false);
            }
        } catch (error) {
            if (!controller.signal.aborted) {
                captureException(error);
            }
        } finally {
            if (!controller.signal.aborted && filingsSymbolRef.current === requestedSymbol) {
                filingsLoadingMoreRef.current = false;
                setLoadingMoreFilings(false);
            }
        }
    }, [filingLookupKey, filingLookupSymbol, getFilingPage, setFilingsHaveMore, sourceFilingCik]);

    const handleCloseFiling = useCallback(() => {
        if (sourceKey) {
            dismissedSourceKeyRef.current = sourceKey;
        }

        setSelectedFiling(undefined);

        if (!sourceKey && !sourceFilingText) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: clearFilingSourceParams(location.search),
                hash: location.hash,
            },
            { replace: true },
        );
    }, [location.hash, location.pathname, location.search, navigate, sourceFilingText, sourceKey]);

    const handleViewInFilings = useCallback(() => {
        if (!filingsPathname) {
            return;
        }

        navigate({
            pathname: filingsPathname,
            search: location.search,
            hash: location.hash,
        });
    }, [filingsPathname, location.hash, location.search, navigate]);

    useEffect(() => {
        if (!drawerOnly || drawerSourceKeyRef.current === sourceKey) {
            return;
        }

        drawerSourceKeyRef.current = sourceKey;
        setSelectedFiling(undefined);
        setSelectedFilingDocument('');
    }, [drawerOnly, sourceKey]);

    useEffect(() => {
        if ((!filingLookupSymbol && !sourceFilingCik) || drawerOnly) {
            return;
        }

        let active = true;
        const controller = new AbortController();

        filingsAbortControllerRef.current?.abort();
        filingsAbortControllerRef.current = controller;
        filingsHaveMoreRef.current = true;
        filingsLoadingMoreRef.current = false;
        filingsSymbolRef.current = filingLookupKey;
        nextFilingPageRef.current = FILING_INITIAL_PAGE_COUNT;
        setFilings([]);
        setFilingsHaveMore(true);
        setLoadingMoreFilings(false);

        const handleGetFilings = async () => {
            try {
                setLoading(true);
                const recentFilings = await getFilingPage(0, controller.signal);

                if (active) {
                    setFilings((currentFilings) => mergeFilings(currentFilings, recentFilings));
                    setLoading(false);
                }

                if (recentFilings.length < FILING_PAGE_SIZE) {
                    setFilingsHaveMore(false);

                    return;
                }

                filingsLoadingMoreRef.current = true;
                setLoadingMoreFilings(true);

                const historicalResults = await Promise.allSettled(
                    Array.from({ length: FILING_INITIAL_PAGE_COUNT - 1 }, (_, index) =>
                        getFilingPage(index + 1, controller.signal),
                    ),
                );

                if (!active) {
                    return;
                }

                const firstRejectedRequest = historicalResults.find(
                    (result): result is PromiseRejectedResult => result.status === 'rejected',
                );

                if (firstRejectedRequest) {
                    captureException(firstRejectedRequest.reason);
                }

                const historicalFilings = historicalResults.flatMap((result) =>
                    result.status === 'fulfilled' ? result.value : [],
                );

                if (historicalFilings.length) {
                    setFilings((currentFilings) => mergeFilings(currentFilings, historicalFilings));
                }

                const loadedPages = historicalResults.flatMap((result) =>
                    result.status === 'fulfilled' ? [result.value] : [],
                );

                if (loadedPages.some((pageFilings) => pageFilings.length < FILING_PAGE_SIZE)) {
                    setFilingsHaveMore(false);
                }
            } catch (error) {
                if (active && !controller.signal.aborted) {
                    captureException(error);
                }
            } finally {
                if (active) {
                    filingsLoadingMoreRef.current = false;
                    setLoading(false);
                    setLoadingMoreFilings(false);
                }
            }
        };

        handleGetFilings();

        return () => {
            active = false;
            controller.abort();
        };
    }, [drawerOnly, filingLookupKey, filingLookupSymbol, getFilingPage, setFilingsHaveMore, sourceFilingCik]);

    useEffect(() => {
        if (!symbol || drawerOnly) {
            return;
        }

        let active = true;
        const controller = new AbortController();

        setLoadingInvestorMaterials(true);

        authorizedAxiosInstance
            .get<SECFiling[] | SECFilingResponseEnvelope>(`/api/vendors/fmp/sec-filings/${symbol}/investor-materials`, {
                params: {
                    from: dayjs().subtract(10, 'year').format('YYYY-MM-DD'),
                    limit: FILING_PAGE_SIZE,
                    page: 0,
                    to: dayjs().format('YYYY-MM-DD'),
                },
                signal: controller.signal,
            })
            .then((response) => {
                if (active) {
                    setFilings((currentFilings) => mergeFilings(currentFilings, getResponseFilings(response.data)));
                }
            })
            .catch((error) => {
                if (active && !controller.signal.aborted) {
                    captureException(error);
                }
            })
            .finally(() => {
                if (active) {
                    setLoadingInvestorMaterials(false);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [drawerOnly, symbol]);

    useEffect(() => {
        if (!drawerOnly || !sourceFilingUrl) {
            return;
        }

        setSelectedFiling({
            acceptedDate: sourceAcceptedDate ?? undefined,
            filingDate: sourceFilingDate ?? undefined,
            finalLink: sourceFilingUrl,
            formType: sourceFilingType ?? undefined,
            symbol: filingLookupSymbol,
            title: `${filingLookupSymbol ?? 'Company'} ${sourceFilingType ?? 'SEC Filing'}`,
        });
    }, [drawerOnly, filingLookupSymbol, sourceAcceptedDate, sourceFilingDate, sourceFilingType, sourceFilingUrl]);

    useEffect(() => {
        if ((!filingLookupSymbol && !sourceFilingCik) || !sourceDateRange || (drawerOnly && sourceFilingUrl)) {
            return;
        }

        let active = true;

        const handleGetHistoricalSourceFiling = async () => {
            try {
                const response: AxiosResponse = await authorizedAxiosInstance.get(
                    `/api/vendors/fmp/sec-filings/${encodeURIComponent(filingLookupSymbol ?? `CIK${sourceFilingCik}`)}`,
                    {
                        params: {
                            cik: sourceFilingCik ?? undefined,
                            limit: 100,
                            page: 0,
                            ...sourceDateRange,
                        },
                    },
                );
                const responseData = Array.isArray(response.data) ? response.data : (response.data?.data ?? []);

                if (active) {
                    setFilings((currentFilings) => mergeFilings(currentFilings, responseData));
                }
            } catch (error) {
                captureException(error);
            }
        };

        handleGetHistoricalSourceFiling();

        return () => {
            active = false;
        };
    }, [drawerOnly, filingLookupSymbol, sourceDateRange, sourceFilingCik, sourceFilingUrl]);

    useEffect(() => {
        if (
            !drawerOnly ||
            !sourceKey ||
            sourceFilingUrl ||
            sourceDateRange ||
            (!filingLookupSymbol && !sourceFilingCik)
        ) {
            return;
        }

        let active = true;
        const controller = new AbortController();

        getFilingPage(0, controller.signal)
            .then((sourceFilings) => {
                if (active) {
                    setFilings((currentFilings) => mergeFilings(currentFilings, sourceFilings));
                }
            })
            .catch((error) => {
                if (active && !controller.signal.aborted) {
                    captureException(error);
                }
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [drawerOnly, filingLookupSymbol, getFilingPage, sourceDateRange, sourceFilingCik, sourceFilingUrl, sourceKey]);

    const selectedFilingUrl = getFilingUrl(selectedFiling);

    useEffect(() => {
        if (drawerOnly && !sourceKey) {
            return;
        }

        fetchMemos();
    }, [drawerOnly, fetchMemos, sourceKey]);

    useEffect(() => {
        if (!sourceKey) {
            dismissedSourceKeyRef.current = undefined;
        }
    }, [sourceKey]);

    useEffect(() => {
        const matchedFiling = filings.find((filing) => matchesFilingSource(filing, sourceLocator));
        const matchedFilingUrl = getFilingUrl(matchedFiling);

        if (
            !shouldOpenFilingFromSource({
                dismissedSourceKey: dismissedSourceKeyRef.current,
                matchedFilingUrl,
                selectedFilingUrl,
                sourceKey,
            })
        ) {
            return;
        }

        if (matchedFiling) {
            setSelectedFiling(matchedFiling);
        }
    }, [filings, selectedFilingUrl, sourceKey, sourceLocator]);

    useEffect(() => {
        if (!selectedFilingUrl) {
            setQuoteSelection(undefined);
            setResolvedSourceLabel(undefined);
            setSourceResolutionAttempted(false);
            setSelectedFilingDocument('');
            setSelectedFilingDocumentUrl('');
            return;
        }

        let active = true;
        let documentObjectUrl: string | undefined;

        const handleGetFilingDocument = async () => {
            try {
                setQuoteSelection(undefined);
                setResolvedSourceLabel(undefined);
                setSourceResolutionAttempted(false);
                setLoadingDocument(true);
                setSelectedFilingDocument('');
                setSelectedFilingDocumentUrl('');

                await authorizedAxiosInstance
                    .get('/api/vendors/fmp/sec-filings/document', {
                        params: {
                            url: selectedFilingUrl,
                        },
                        responseType: 'blob',
                    })
                    .then(async (res: AxiosResponse<Blob>) => {
                        if (res.status !== 200 || !active) {
                            return;
                        }

                        const contentType = String(res.headers['content-type'] ?? res.data.type ?? '').toLowerCase();

                        if (contentType.includes('application/pdf')) {
                            documentObjectUrl = URL.createObjectURL(res.data);
                            setSelectedFilingDocumentUrl(documentObjectUrl);
                            return;
                        }

                        setSelectedFilingDocument(await res.data.text());
                    });
            } catch (error) {
                if (active) {
                    captureException(error);
                    setSelectedFilingDocument('');
                    setSelectedFilingDocumentUrl('');
                }
            } finally {
                if (active) {
                    setLoadingDocument(false);
                }
            }
        };

        handleGetFilingDocument();

        return () => {
            active = false;

            if (documentObjectUrl) {
                URL.revokeObjectURL(documentObjectUrl);
            }
        };
    }, [selectedFilingUrl]);

    const filingSourceUrl = useCallback(
        (quote?: string) => {
            const url = new URL(window.location.href);

            if (selectedFilingUrl) {
                url.searchParams.set('filingUrl', selectedFilingUrl);
            }

            if (quote) {
                url.searchParams.set('filingText', normalizeSelectionText(quote).slice(0, 240));
            }

            return url.toString();
        },
        [selectedFilingUrl],
    );

    const filingCommentHref = useCallback(
        (quote?: string) => {
            const params = new URLSearchParams(location.search);

            if (selectedFilingUrl) {
                params.set('filingUrl', selectedFilingUrl);
            }

            if (quote) {
                params.set('filingText', normalizeSelectionText(quote).slice(0, 240));
            } else {
                params.delete('filingText');
            }

            const search = params.toString();

            return `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
        },
        [location.hash, location.pathname, location.search, selectedFilingUrl],
    );

    const filingCommentTarget = useMemo<ContextualCommentTarget>(() => {
        const filingType = selectedFiling ? getFilingType(selectedFiling) || 'Filing' : 'Filing';
        const filingYear = dayjs(selectedFiling?.filingDate ?? selectedFiling?.acceptedDate).isValid()
            ? dayjs(selectedFiling?.filingDate ?? selectedFiling?.acceptedDate).year()
            : undefined;
        const entityLabel = sourceEntity?.name ?? symbol ?? 'Company';
        const filingLabel = `${entityLabel} - ${filingType}${filingYear ? ` (${filingYear})` : ''}`;
        const quote = quoteSelection?.text;

        return {
            component: 'filings',
            label: filingLabel,
            metadata: {
                selection: quote
                    ? {
                          filingUrl: selectedFilingUrl,
                          kind: 'filing-text-selection',
                          quote,
                      }
                    : undefined,
                source: 'investment-filing',
                target: {
                    filingUrl: selectedFilingUrl,
                    href: filingCommentHref(quote),
                    label: filingLabel,
                    nodeType: 'filing',
                    quote,
                    type: quote ? 'text-selection' : 'Filing',
                },
            },
            type: quote ? 'Filing selection' : 'Filing',
        };
    }, [filingCommentHref, quoteSelection?.text, selectedFiling, selectedFilingUrl, sourceEntity?.name, symbol]);

    const scrollFrameToQuote = useCallback((quote?: string | null) => {
        const target = normalizeSelectionText(quote ?? '').slice(0, 240);
        const iframeWindow = iframeRef.current?.contentWindow as FindableWindow | null;

        const findInFrame = iframeWindow?.find;

        if (!target || !iframeWindow || typeof findInFrame !== 'function') {
            return;
        }

        requestAnimationFrame(() => {
            iframeWindow.focus();
            const found = findInFrame.call(iframeWindow, target, false, false, true, false, true, false);

            if (found) {
                iframeWindow
                    .getSelection()
                    ?.anchorNode?.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        });
    }, []);

    const readFrameSelection = useCallback(() => {
        const iframe = iframeRef.current;
        const viewer = viewerRef.current;
        const iframeWindow = iframe?.contentWindow;
        const selection = iframeWindow?.getSelection();

        if (!iframe || !viewer || !selection || selection.isCollapsed || selection.rangeCount === 0) {
            setQuoteSelection(undefined);
            return;
        }

        const text = normalizeSelectionText(selection.toString());

        if (text.length < 2) {
            setQuoteSelection(undefined);
            return;
        }

        const rangeRect = selection.getRangeAt(0).getBoundingClientRect();
        const iframeRect = iframe.getBoundingClientRect();
        const viewerRect = viewer.getBoundingClientRect();

        setQuoteSelection({
            text,
            left: Math.max(12, iframeRect.left - viewerRect.left + rangeRect.left),
            top: Math.max(12, iframeRect.top - viewerRect.top + rangeRect.top - 42),
        });
    }, []);

    const handleFrameLoad = useCallback(() => {
        const iframeDocument = iframeRef.current?.contentDocument;

        if (iframeDocument) {
            applyLightFilingDocumentSurface(iframeDocument);
        }

        iframeDocument?.addEventListener('mouseup', readFrameSelection);
        iframeDocument?.addEventListener('keyup', readFrameSelection);

        if (iframeDocument && sourceFinancialLocator) {
            requestAnimationFrame(() => {
                const sourceMatch = findFilingFinancialSource(iframeDocument, sourceFinancialLocator);

                setSourceResolutionAttempted(true);

                if (sourceMatch) {
                    setResolvedSourceLabel(sourceMatch.asReportedLabel);
                    focusFilingFinancialSource(sourceMatch);
                    return;
                }

                setResolvedSourceLabel(undefined);
            });
            return;
        }

        if (iframeDocument && sourceStatementSectionLocator) {
            requestAnimationFrame(() => {
                const sourceMatch = findFilingFinancialStatementSection(iframeDocument, sourceStatementSectionLocator);

                setSourceResolutionAttempted(true);

                if (sourceMatch) {
                    setResolvedSourceLabel(sourceMatch.label);
                    focusFilingFinancialStatementSection(sourceMatch);
                    return;
                }

                setResolvedSourceLabel(undefined);
            });
            return;
        }

        setSourceResolutionAttempted(false);
        scrollFrameToQuote(sourceFilingText);
    }, [
        readFrameSelection,
        scrollFrameToQuote,
        sourceFilingText,
        sourceFinancialLocator,
        sourceStatementSectionLocator,
    ]);

    const quoteHtml = useCallback(
        (quote: string) => {
            return getFilingQuoteHtml({
                quote,
                selectedFiling,
                sourceEntity,
                sourceUrl: filingSourceUrl(quote),
                symbol,
            });
        },
        [filingSourceUrl, selectedFiling, sourceEntity, symbol],
    );

    const { appendQuoteToMemo, busy, copyQuote, copySourceLink, createMemoFromQuote, pendingAction } =
        useSourceQuoteActions({
            getQuoteHtml: quoteHtml,
            getSourceLink: filingSourceUrl,
            memoTitle: `${symbol ?? 'SEC'} ${selectedFiling ? getFilingType(selectedFiling) : 'Filing'} Filing Notes`,
            selectedText: quoteSelection?.text,
            sourceKey: selectedFilingUrl || '',
        });

    const quoteMemoPanel = (
        <QuoteMemoDropdownPanel
            active={Boolean(quoteSelection?.text)}
            memos={memos}
            pendingAction={pendingAction}
            loadingMemos={loadingMemos}
            onCopy={copyQuote}
            onCreate={() => createMemoFromQuote()}
            onSelectMemo={(memo) => appendQuoteToMemo(memo)}
        />
    );

    const actionRow = () => (
        <Row
            align='middle'
            gutter={[8, 8]}
            style={{
                padding: '0.75rem 1rem',
                borderBottom: `1px solid ${token.colorBorder}`,
            }}
            wrap={false}
        >
            <Col flex='auto'>
                <Space size={8} wrap>
                    <Button
                        size='small'
                        icon={<TbLink style={{ verticalAlign: '-0.125rem' }} />}
                        disabled={!selectedFilingUrl || busy}
                        loading={pendingAction?.type === 'copy-source'}
                        onClick={copySourceLink}
                    >
                        Copy Source Link
                    </Button>
                    {selectedFilingUrl && (
                        <a href={selectedFilingUrl} target='_blank' rel='noreferrer'>
                            <Button size='small' icon={<TbExternalLink style={{ verticalAlign: '-0.125rem' }} />}>
                                Open SEC Filing
                            </Button>
                        </a>
                    )}
                    {sourceFinancialLocator && resolvedSourceLabel ? (
                        <Space size={4}>
                            <Tag color='blue' style={{ marginRight: 0 }}>
                                As reported
                            </Tag>
                            <Typography.Text
                                ellipsis
                                style={{ color: token.colorTextSecondary, maxWidth: '24rem' }}
                                title={resolvedSourceLabel}
                            >
                                {resolvedSourceLabel}
                            </Typography.Text>
                        </Space>
                    ) : null}
                    {sourceStatementSectionLocator && resolvedSourceLabel ? (
                        <Space size={4}>
                            <Tag color='blue' style={{ marginRight: 0 }}>
                                Statement section
                            </Tag>
                            <Typography.Text
                                ellipsis
                                style={{ color: token.colorTextSecondary, maxWidth: '24rem' }}
                                title={resolvedSourceLabel}
                            >
                                {resolvedSourceLabel}
                            </Typography.Text>
                        </Space>
                    ) : null}
                    {sourceFinancialLocator && sourceResolutionAttempted && !resolvedSourceLabel ? (
                        <Typography.Text style={{ color: token.colorTextTertiary }}>
                            Exact reported row unavailable
                        </Typography.Text>
                    ) : null}
                    {sourceStatementSectionLocator && sourceResolutionAttempted && !resolvedSourceLabel ? (
                        <Typography.Text style={{ color: token.colorTextTertiary }}>
                            Statement section unavailable
                        </Typography.Text>
                    ) : null}
                </Space>
            </Col>
            <Col flex='none'>
                <Space size={4}>
                    {commentResource && selectedFilingUrl ? (
                        <ContextualCommentButton
                            resource={commentResource}
                            target={filingCommentTarget}
                            tooltipPlacement='bottomRight'
                        />
                    ) : null}
                    <Dropdown
                        popupRender={() => quoteMemoPanel}
                        trigger={['click']}
                        disabled={!quoteSelection?.text || busy}
                    >
                        <DropdownButton
                            size='small'
                            type='primary'
                            disabled={!quoteSelection?.text || busy}
                            loading={busy && pendingAction?.type !== 'copy-source'}
                            icon={<TbQuote style={{ verticalAlign: '-0.125rem' }} />}
                        >
                            Quote Selection
                        </DropdownButton>
                    </Dropdown>
                </Space>
            </Col>
        </Row>
    );

    const deferredFilingQuery = useDeferredValue(filingQuery);
    const filteredFilings = useMemo(
        () => filterFilings(filings, deferredFilingQuery, filingFilters),
        [deferredFilingQuery, filingFilters, filings],
    );
    const filingSections = useMemo(
        () => getPopulatedFilingSections(filteredFilings, filingCategories),
        [filteredFilings],
    );
    const filingListStateKey = useMemo(
        () => `${deferredFilingQuery}:${JSON.stringify(filingFilters)}`,
        [deferredFilingQuery, filingFilters],
    );

    const setCategoryOpen = useCallback(
        (categoryKey: string, open: boolean) => {
            setOpenCategoryKeys((currentKeys) => {
                const nextKeys = new Set(currentKeys);

                if (open) {
                    nextKeys.add(categoryKey);
                } else {
                    nextKeys.delete(categoryKey);
                }

                return nextKeys;
            });

            if (open && categoryKey !== 'investor-materials') {
                void loadMoreFilings();
            }
        },
        [loadMoreFilings],
    );

    return (
        <div
            style={
                drawerOnly
                    ? { display: 'contents' }
                    : { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }
            }
        >
            {!drawerOnly && (
                <>
                    <FilingFiltersToolbar
                        filings={filings}
                        filters={filingFilters}
                        onFiltersChange={setFilingFilters}
                        onQueryChange={setFilingQuery}
                        query={filingQuery}
                    />
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflowX: 'hidden',
                            overflowY: 'auto',
                        }}
                    >
                        {loading ? (
                            <BaseSkeleton fade paragraph={{ rows: 36 }} title />
                        ) : filingSections.length ? (
                            <div
                                style={{
                                    backgroundColor: token.colorBgContainer,
                                    borderBottom: `1px solid ${token.colorBorder}`,
                                }}
                            >
                                {filingSections.map(({ category, filings: categoryFilings }, index) => {
                                    const CategoryIcon = category.icon ?? TbFileText;

                                    return (
                                        <CompactCollapsible
                                            contentStyle={{ padding: 0 }}
                                            destroyOnHidden
                                            headerExtra={
                                                <Tag style={{ marginRight: 0 }}>
                                                    {category.key === 'investor-materials' && loadingInvestorMaterials
                                                        ? '…'
                                                        : categoryFilings.length}
                                                </Tag>
                                            }
                                            icon={
                                                <Avatar
                                                    icon={<CategoryIcon size='0.75rem' />}
                                                    shape='square'
                                                    size={20}
                                                    style={{
                                                        alignItems: 'center',
                                                        backgroundColor: token.colorBorderBg,
                                                        border: `1px solid ${token.colorBorder}`,
                                                        color: token.colorTextTertiary,
                                                        display: 'inline-flex',
                                                        justifyContent: 'center',
                                                    }}
                                                />
                                            }
                                            key={category.key}
                                            label={category.title}
                                            open={openCategoryKeys.has(category.key)}
                                            sectionStyle={index === 0 ? { borderTop: 'none' } : undefined}
                                            onOpenChange={(open) => setCategoryOpen(category.key, open)}
                                        >
                                            {category.key === 'investor-materials' && loadingInvestorMaterials ? (
                                                <div style={{ padding: '0.75rem' }}>
                                                    <BaseSkeleton fade paragraph={{ rows: 8 }} title={false} />
                                                </div>
                                            ) : categoryFilings.length === 0 ? (
                                                <EmptySection
                                                    description='No qualifying Form 8-K investor-relations exhibits were found in the recent filing history.'
                                                    height='12rem'
                                                    icon={<CategoryIcon />}
                                                    label='No Investor Materials Found'
                                                />
                                            ) : (
                                                <List
                                                    dataSource={categoryFilings}
                                                    estimateSize={64}
                                                    getItemKey={(filing) => getFilingIdentity(filing)}
                                                    hasMore={
                                                        category.key === 'investor-materials' ? false : hasMoreFilings
                                                    }
                                                    height={
                                                        categoryFilings.length > FILING_SECTION_PAGE_SIZE
                                                            ? FILING_SECTION_SCROLL_HEIGHT
                                                            : undefined
                                                    }
                                                    key={`${category.key}:${filingListStateKey}`}
                                                    loadingMore={
                                                        category.key === 'investor-materials'
                                                            ? false
                                                            : loadingMoreFilings
                                                    }
                                                    onEndReached={
                                                        category.key === 'investor-materials'
                                                            ? undefined
                                                            : loadMoreFilings
                                                    }
                                                    pageSize={FILING_SECTION_PAGE_SIZE}
                                                    paginate
                                                    renderItem={(filing) => (
                                                        <List.Item
                                                            actions={[
                                                                <Button
                                                                    key='view'
                                                                    size='small'
                                                                    type='text'
                                                                    onClick={() => setSelectedFiling(filing)}
                                                                    icon={
                                                                        <TbFileText
                                                                            style={{ verticalAlign: '-0.125rem' }}
                                                                        />
                                                                    }
                                                                >
                                                                    View
                                                                </Button>,
                                                            ]}
                                                            style={{ paddingInline: '0.75rem' }}
                                                        >
                                                            <List.Item.Meta
                                                                title={
                                                                    <Space size={8}>
                                                                        <Tag>{getFilingType(filing) || 'Filing'}</Tag>
                                                                        <Typography.Text>
                                                                            {filing.title ||
                                                                                `${symbol} ${getFilingType(filing)}`}
                                                                        </Typography.Text>
                                                                    </Space>
                                                                }
                                                                description={
                                                                    <span>
                                                                        <span>
                                                                            {filing.filingDate
                                                                                ? dayjs(filing.filingDate).format('LL')
                                                                                : 'Date unavailable'}
                                                                        </span>
                                                                        {filing.acceptedDate && (
                                                                            <>
                                                                                <MiddleDot horizontalMargin='0.5rem' />
                                                                                <span
                                                                                    style={{
                                                                                        color: token.colorTextTertiary,
                                                                                    }}
                                                                                >
                                                                                    Accepted{' '}
                                                                                    {dayjs(filing.acceptedDate).format(
                                                                                        'LLL',
                                                                                    )}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </span>
                                                                }
                                                            />
                                                        </List.Item>
                                                    )}
                                                />
                                            )}
                                        </CompactCollapsible>
                                    );
                                })}
                            </div>
                        ) : (
                            <EmptySection
                                description={
                                    filingQuery || filingFilters.length
                                        ? 'No filings match the current search and filters.'
                                        : 'No filings are available for this company.'
                                }
                                height='100%'
                                icon={<TbFileText style={{ color: token.colorTextTertiary }} />}
                                label={
                                    filingQuery || filingFilters.length ? 'No Filings Found' : 'No Filings Available'
                                }
                            />
                        )}
                    </div>
                </>
            )}
            <Drawer
                title={
                    selectedFiling ? (
                        <Space size={8}>
                            <Tag>{getFilingType(selectedFiling) || 'Filing'}</Tag>
                            <span>{selectedFiling.title || `${symbol} Filing`}</span>
                        </Space>
                    ) : sourceFilingType ? (
                        <Space size={8}>
                            <Tag>{sourceFilingType}</Tag>
                            <span>{`${symbol ?? 'Company'} Filing`}</span>
                        </Space>
                    ) : (
                        'Filing'
                    )
                }
                placement='right'
                width='64vw'
                open={Boolean(selectedFiling) || resolvingSourceFiling}
                onClose={handleCloseFiling}
                extra={
                    canViewInFilings || selectedFilingUrl ? (
                        <Space size='small'>
                            {canViewInFilings ? (
                                <Button icon={<TbFileStack size='1rem' />} onClick={handleViewInFilings} size='small'>
                                    View in Filings
                                </Button>
                            ) : null}
                            {selectedFilingUrl ? (
                                <a href={selectedFilingUrl} target='_blank' rel='noreferrer'>
                                    <Button
                                        size='small'
                                        icon={<TbExternalLink style={{ verticalAlign: '-0.125rem' }} />}
                                    >
                                        Open
                                    </Button>
                                </a>
                            ) : null}
                        </Space>
                    ) : undefined
                }
                bodyStyle={{ overflow: 'hidden', padding: 0 }}
                footerStyle={{ borderTop: 0, padding: 0 }}
                footer={
                    <DocumentReaderFooter
                        available={Boolean(selectedFilingDocument || selectedFilingDocumentUrl)}
                        date={
                            selectedFiling?.filingDate || selectedFiling?.acceptedDate || sourceFilingDate || undefined
                        }
                        dateLabel='Filed'
                        documentType={selectedFiling ? getFilingType(selectedFiling) : sourceFilingType || 'Filing'}
                        loading={loadingDocument || resolvingSourceFiling}
                        publisher={selectedFiling?.discoverySource === 'fmp' ? 'SEC / Platform Financials' : 'SEC'}
                        selectedText={quoteSelection?.text}
                        title={sourceEntity?.name || selectedFiling?.title || selectedFiling?.symbol || symbol}
                    />
                }
            >
                {loadingDocument || resolvingSourceFiling ? (
                    <div style={{ padding: '1.5rem' }}>
                        <BaseSkeleton fade paragraph={{ rows: 24 }} title />
                    </div>
                ) : selectedFilingDocument || selectedFilingDocumentUrl ? (
                    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
                        {actionRow()}
                        <div
                            ref={viewerRef}
                            style={{
                                flex: 1,
                                margin: '1rem',
                                minHeight: 0,
                                overflow: 'hidden',
                                position: 'relative',
                                border: `1px dashed ${token.colorBorder}`,
                                borderRadius: token.borderRadiusLG,
                                backgroundColor: FILING_DOCUMENT_BACKGROUND,
                            }}
                        >
                            <iframe
                                ref={iframeRef}
                                sandbox='allow-popups allow-same-origin'
                                title={selectedFiling?.title || `${symbol} SEC filing`}
                                src={selectedFilingDocumentUrl || undefined}
                                srcDoc={selectedFilingDocumentUrl ? undefined : selectedFilingDocument}
                                onLoad={handleFrameLoad}
                                style={{
                                    backgroundColor: FILING_DOCUMENT_BACKGROUND,
                                    border: 0,
                                    colorScheme: 'only light',
                                    display: 'block',
                                    height: '100%',
                                    width: '100%',
                                }}
                            />
                            {quoteSelection?.text && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        left: quoteSelection.left,
                                        top: quoteSelection.top,
                                        zIndex: 2,
                                        padding: '0.25rem',
                                        border: `1px solid ${token.colorBorder}`,
                                        borderRadius: token.borderRadius,
                                        backgroundColor: token.colorBgElevated,
                                        boxShadow: token.boxShadowSecondary,
                                    }}
                                >
                                    <Space size={4}>
                                        <Button
                                            size='small'
                                            type='primary'
                                            icon={<TbFilePlus style={{ verticalAlign: '-0.125rem' }} />}
                                            disabled={busy}
                                            loading={pendingAction?.type === 'create-memo'}
                                            onClick={createMemoFromQuote}
                                        >
                                            New Memo
                                        </Button>
                                        <Dropdown
                                            popupRender={() => quoteMemoPanel}
                                            trigger={['click']}
                                            disabled={busy}
                                        >
                                            <DropdownButton
                                                size='small'
                                                disabled={busy}
                                                loading={pendingAction?.type === 'add-to-memo'}
                                            >
                                                Add to Memo
                                            </DropdownButton>
                                        </Dropdown>
                                        <Button
                                            size='small'
                                            icon={<TbCopy style={{ verticalAlign: '-0.125rem' }} />}
                                            aria-label='Copy quote and source'
                                            disabled={busy}
                                            loading={pendingAction?.type === 'copy-quote'}
                                            onClick={copyQuote}
                                        />
                                        {commentResource ? (
                                            <ContextualCommentButton
                                                resource={commentResource}
                                                target={filingCommentTarget}
                                                tooltipPlacement='top'
                                            />
                                        ) : null}
                                    </Space>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <EmptySection
                        height='100%'
                        icon={<TbFileText />}
                        label='Document unavailable'
                        description='This filing document could not be loaded from the source provider.'
                    />
                )}
            </Drawer>
        </div>
    );
};

export default SECFilingsExplorer;
